import { DomainError } from '../errors.js';
import type { LocalDate } from '../local-date.js';
import { isValidRunningHours } from '../running-hours.js';
import { type CounterReading, cumulativeHours } from './counters.js';
import { EQUIPMENT_STATUSES, type EquipmentStatus } from './equipment.js';
import type { ChecklistItemKind } from './jobs.js';
import type { DueFields } from './plan.js';
import type { WorkOrderKind } from './work-order.js';

/**
 * İş kaydı (plan M7 Task 8; ADR 0029 §3; K4, K7, K14, K24). Raporlanan iş kaydı değiştirilemez
 * [NR496 4.5.3f]; içeriği raporlama anındaki kapalı kümedir ve hash'i sonra değişmez. Saf modül.
 */

export const WORK_RESULTS = ['pass', 'fail'] as const;
export type WorkResult = (typeof WORK_RESULTS)[number];

/** Kontrol listesi maddesinin cevabı: uygun, uygun değil, uygulanmaz. */
export const CHECKLIST_ANSWERS = ['pass', 'fail', 'na'] as const;
export type ChecklistAnswer = (typeof CHECKLIST_ANSWERS)[number];

export interface ChecklistResult {
  index: number;
  answer: ChecklistAnswer;
  value: number | null;
  remark: string | null;
}

export interface Performer {
  /** Gemideki kişi; yüklenici ya da servis mühendisi ise boş. */
  userId: string | null;
  name: string;
}

export interface SparePartUsed {
  materialId: string;
  quantity: number;
}

export interface WorkRecordInput {
  /** İş emrinin türü (K24). */
  kind: WorkOrderKind;
  /** Yapılış anı, ISO-8601 UTC. */
  doneAt: string;
  /** Gemi saati farkı, dakika. */
  tzOffsetMin: number;
  /** Yapılış günü: gemi yerel tarihi (CLAUDE.md "Zaman"). */
  doneOn: LocalDate;
  /** Kayıt anında sayacın gösterdiği değer (K4). */
  meterHours: number | null;
  performedBy: Performer[];
  workPerformed: string;
  checklistResult: ChecklistResult[];
  result: WorkResult;
  /** Onarım sonrası durum [NR496 4.3.2d, 4.5.4d] (K24). */
  conditionAfter: EquipmentStatus | null;
  sparePartsUsed: SparePartUsed[];
  remarks: string | null;
  /**
   * Gecikme gerekçesi [NR496 4.5.4b, 4.6.2a] (ADR 0029 Ek M7f F1): gecikmiş işte zorunlu. M7f'den
   * önceki kayıtta yoktur; içeriğe yalnız doluyken girer.
   */
  overdueReason?: string | null;
  /**
   * Durum bazlı işte CM tanı/öngörü raporu servis sağlayıcıdan bekleniyor beyanı [NR496 4.5.4e]
   * (F4). İçeriğe yalnız `true` iken girer.
   */
  cmReportPending?: boolean;
  /**
   * Bakımdan önceki durum (ADR 0132 §2; DNV MAD `conditionbefore`): isteğe bağlı, onarım sonrası
   * durumla aynı üç değer. 0090'dan önceki kayıtta yoktur; içeriğe yalnız doluyken girer.
   */
  conditionBefore?: EquipmentStatus | null;
  /**
   * Gerçek adam-saat (ADR 0132 §2; DNV MAD `workhours`): isteğe bağlı, 0 < h <= 10000. İçeriğe
   * yalnız doluyken girer.
   */
  workHours?: number | null;
}

/** Gerçek adam-saatin üst sınırı (ADR 0132 §2): bir iş kaydı için makul en büyük değer. */
export const MAX_WORK_HOURS = 10_000;

export interface WorkRecordContext {
  /** Gemi sunucusunun saati, ISO-8601 UTC (mimari belge §6.11). */
  serverNow: string;
  /** İşin çalışma saati tetikleyicisi var mı. */
  hoursBased: boolean;
  /** İş tanımının kontrol listesi. */
  checklist: readonly { text: string; kind: ChecklistItemKind }[];
  /** İş gecikmiş raporlanıyor (`workRecordOverdue`; F1): gecikme gerekçesi zorunlu. */
  overdue?: boolean;
  /**
   * Durum bazlı iş (F4): `required` ise iş emrinde `report` türü ek (`attached`) ya da
   * "sağlayıcıdan bekleniyor" beyanı zorunlu.
   */
  cmReport?: { required: boolean; attached: boolean };
}

/**
 * İş gecikmiş mi raporlanıyor [NR496 4.5.4b] (ADR 0029 Ek M7f F1): raporlama günü (kaydın gemi
 * günü) vadeden sonraysa ya da yapılıştaki birikmiş saat vade saatini aştıysa. Yapılış günü
 * raporlama gününden sonra olamadığı için "vadeden sonra yapılan" da kapsanır. Plansız iş emrinde
 * vade hedef tarihtir (F3). Vadesiz iş gecikmez.
 */
export function workRecordOverdue(input: {
  reportedOn: LocalDate;
  runningHours: number | null;
  dueOn: LocalDate | null;
  dueHours: number | null;
}): boolean {
  if (input.dueOn !== null && input.reportedOn > input.dueOn) return true;
  return (
    input.runningHours !== null && input.dueHours !== null && input.runningHours > input.dueHours
  );
}

/** UTC an + gemi saati farkı → gemi yerel tarihi. */
export function shipLocalDate(at: string, tzOffsetMin: number): LocalDate {
  return new Date(Date.parse(at) + tzOffsetMin * 60_000).toISOString().slice(0, 10);
}

const blank = (value: string | null | undefined) => !value?.trim();

/**
 * İş kaydının kuralları (plan Task 8):
 * - Yapılış günü yapılış anının gemi yerel tarihidir; an gemi sunucusu saatine göre gelecekte
 *   olamaz (§6.11).
 * - Saat bazlı işte sayaç değeri zorunlu (`RUNNING_HOURS_REQUIRED`).
 * - En az bir uygulayıcı ve iş açıklaması.
 * - Kontrol listesinin her maddesi bir kez cevaplanır; FAIL açıklama, değer maddesi değer ister;
 *   FAIL maddesi varken sonuç PASS olamaz.
 * - Onarım sonrası durum her iş kaydında zorunlu [NR496 4.3.2d, 4.5.4d] (K24; ADR 0029 Ek M7f F2).
 * - Gecikmiş işte gecikme gerekçesi (`OVERDUE_REASON_REQUIRED`) [NR496 4.5.4b] (F1).
 * - Durum bazlı işte CM raporu eki ya da beyanı (`CM_REPORT_REQUIRED`) [NR496 4.5.4e] (F4).
 */
export function validateWorkRecord(input: WorkRecordInput, context: WorkRecordContext): void {
  const at = Date.parse(input.doneAt);
  if (Number.isNaN(at) || !Number.isInteger(input.tzOffsetMin)) {
    throw new DomainError('INVALID_DATE', String(input.doneAt));
  }
  if (input.doneOn !== shipLocalDate(input.doneAt, input.tzOffsetMin)) {
    throw new DomainError('DONE_ON_MISMATCH', input.doneOn);
  }
  if (at > Date.parse(context.serverNow)) throw new DomainError('FUTURE_DATE', input.doneAt);

  if (input.meterHours === null) {
    if (context.hoursBased) throw new DomainError('RUNNING_HOURS_REQUIRED');
  } else {
    if (!isValidRunningHours(input.meterHours)) throw new DomainError('INVALID_HOURS');
    if (input.meterHours < 0) throw new DomainError('NEGATIVE_VALUE');
  }

  if (input.performedBy.length === 0 || input.performedBy.some((p) => blank(p.name))) {
    throw new DomainError('PERFORMER_REQUIRED');
  }
  if (blank(input.workPerformed)) throw new DomainError('WORK_DESCRIPTION_REQUIRED');

  const answers = new Map<number, ChecklistResult>();
  for (const item of input.checklistResult) {
    if (
      !Number.isInteger(item.index) ||
      item.index < 0 ||
      item.index >= context.checklist.length ||
      answers.has(item.index) ||
      !(CHECKLIST_ANSWERS as readonly string[]).includes(item.answer)
    ) {
      throw new DomainError('CHECKLIST_INVALID', String(item.index));
    }
    answers.set(item.index, item);
  }
  if (answers.size !== context.checklist.length) throw new DomainError('CHECKLIST_INCOMPLETE');
  for (const [index, item] of [...answers].sort(([a], [b]) => a - b)) {
    if (item.answer === 'fail' && blank(item.remark)) {
      throw new DomainError('CHECKLIST_REMARK_REQUIRED', String(index));
    }
    const kind = context.checklist[index]?.kind;
    if (
      kind === 'value' &&
      item.answer !== 'na' &&
      (typeof item.value !== 'number' || !Number.isFinite(item.value))
    ) {
      throw new DomainError('CHECKLIST_VALUE_REQUIRED', String(index));
    }
  }

  if (!(WORK_RESULTS as readonly string[]).includes(input.result)) {
    throw new DomainError('RESULT_REQUIRED');
  }
  if (input.result === 'pass' && [...answers.values()].some((a) => a.answer === 'fail')) {
    throw new DomainError('RESULT_INCONSISTENT');
  }

  if (input.conditionAfter !== null) {
    if (!(EQUIPMENT_STATUSES as readonly string[]).includes(input.conditionAfter)) {
      throw new DomainError('INVALID_CONDITION', String(input.conditionAfter));
    }
  } else {
    // [NR496 4.3.2d, 4.5.4d] (K24): en sıkı okuma, her iş kapanışında (ADR 0029 Ek M7f F2)
    throw new DomainError('CONDITION_AFTER_REQUIRED');
  }

  // Bakım öncesi durum ve adam-saat isteğe bağlıdır (ADR 0132 §2)
  if (
    input.conditionBefore !== undefined &&
    input.conditionBefore !== null &&
    !(EQUIPMENT_STATUSES as readonly string[]).includes(input.conditionBefore)
  ) {
    throw new DomainError('INVALID_CONDITION', String(input.conditionBefore));
  }
  if (
    input.workHours !== undefined &&
    input.workHours !== null &&
    !(
      typeof input.workHours === 'number' &&
      Number.isFinite(input.workHours) &&
      input.workHours > 0 &&
      input.workHours <= MAX_WORK_HOURS
    )
  ) {
    throw new DomainError('INVALID_WORK_HOURS', String(input.workHours));
  }

  // [NR496 4.5.4b] "the reason for delay is to be compulsorily imputed by the user" (F1)
  if (context.overdue === true && blank(input.overdueReason)) {
    throw new DomainError('OVERDUE_REASON_REQUIRED');
  }
  // [NR496 4.5.4e] (F4): rapor sonra gelecekse beyan edilir; beyan zilde uyarı olarak kalır
  if (
    context.cmReport?.required === true &&
    !context.cmReport.attached &&
    input.cmReportPending !== true
  ) {
    throw new DomainError('CM_REPORT_REQUIRED');
  }

  for (const part of input.sparePartsUsed) {
    if (
      typeof part.materialId !== 'string' ||
      typeof part.quantity !== 'number' ||
      !(part.quantity > 0) ||
      part.quantity > 1_000_000
    ) {
      throw new DomainError('INVALID_SPARE_PARTS');
    }
  }
}

/**
 * Sayaç değerinin birikmiş toplama çevrilmesi (K4): yapılış anından önceki sayaç olaylarının
 * ofseti eklenir. Örnek: eski son 25.000, yeni başlangıç 0, değer 150 → 25.150.
 */
export function recordRunningHours(
  meterHours: number,
  doneAt: string,
  history: readonly CounterReading[],
): number {
  return cumulativeHours({ kind: 'reading', readAt: doneAt, totalHours: meterHours }, history);
}

/**
 * İş kaydının saati sayacın o anki birikmiş toplamını aşamaz (K4): sayaçta görülen değer önce
 * okuma olarak girilir. Sayacın okuması yoksa saat bazlı kayıt yazılamaz.
 */
export function assertHoursNotAfterCurrent(
  runningHours: number,
  currentCumulative: number | undefined,
): void {
  if (currentCumulative === undefined || runningHours > currentCumulative) {
    throw new DomainError('HOURS_AFTER_CURRENT', `${runningHours} > ${String(currentCumulative)}`);
  }
}

export interface WorkRecordContentInput {
  vesselId: string;
  workOrderId: string;
  woNo: string;
  jobDefinitionId: string | null;
  equipmentId: string;
  record: WorkRecordInput;
  /** Sunucunun çevirdiği birikmiş toplam (K4). */
  runningHours: number | null;
  /**
   * Sunucunun plan motorundan doldurduğu vade alanları (K39). İş kaydında döngü kolonu yoktur
   * (kaydın kendisi sonraki döngünün tabanıdır): içerik saklanan dört alandan oluşur.
   */
  due: Omit<DueFields, 'cycleBaseRecordId'> & { cycleBaseRecordId?: string | null };
  instruction: { revisionId: string | null; sha256: string | null };
  supersedesRecordId: string | null;
  /** Başmühendis sörveyinin kayıt anındaki sonucu (K31; M7d'de dolar). */
  ce: {
    ceSurvey: boolean;
    ceCredit: boolean | null;
    ceAuthorizationId: string | null;
    classItemCode: string | null;
    classItemCategory: string | null;
    ceRejectionCode: string | null;
  };
  /** İş emrinin iptal edilmemiş ölçümleri: değer ve limit anlık kopyası (M7c'de dolar). */
  measurements: readonly ({ id: string } & Record<string, unknown>)[];
  /** İş emrinin iptal edilmemiş ekleri (M7b2'de dolar). */
  attachments: readonly { id: string; sha256: string }[];
}

/**
 * İş kaydının içeriği (K14; ADR 0029 §3): raporlama anındaki kapalı küme — kayıt alanları, vade
 * alanları, CE kopya kolonları, iş emrinin iptal edilmemiş ölçümleri ve eklerinin SHA-256'ları
 * (kimliğe göre sıralı) ve talimat revizyonu. Hash'i (`contentHash`) `work_records.content_hash`'e
 * yazılır ve sonra değişmez [NR496 4.5.3f]. Gönderim ve onay bu hash'i taşır (mimari belge §7).
 */
export function workRecordContent(input: WorkRecordContentInput) {
  const byId = <T extends { id: string }>(list: readonly T[]) =>
    [...list].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const { record } = input;
  return {
    vesselId: input.vesselId,
    workOrderId: input.workOrderId,
    woNo: input.woNo,
    jobDefinitionId: input.jobDefinitionId,
    equipmentId: input.equipmentId,
    kind: record.kind,
    doneOn: record.doneOn,
    doneAt: record.doneAt,
    tzOffsetMin: record.tzOffsetMin,
    meterHours: record.meterHours,
    runningHours: input.runningHours,
    performedBy: record.performedBy.map((p) => ({ userId: p.userId, name: p.name })),
    workPerformed: record.workPerformed,
    checklistResult: [...record.checklistResult]
      .sort((a, b) => a.index - b.index)
      .map((c) => ({ index: c.index, answer: c.answer, value: c.value, remark: c.remark })),
    result: record.result,
    conditionAfter: record.conditionAfter,
    sparePartsUsed: record.sparePartsUsed.map((p) => ({
      materialId: p.materialId,
      quantity: p.quantity,
    })),
    remarks: record.remarks,
    // Yalnız doluyken (F1, F4): M7f'den önceki kaydın içeriği ve hash'i değişmez [NR496 4.5.3f]
    ...(typeof record.overdueReason === 'string' ? { overdueReason: record.overdueReason } : {}),
    ...(record.cmReportPending === true ? { cmReportPending: true as const } : {}),
    // Yalnız doluyken (ADR 0132 §2): 0090'dan önceki kaydın içeriği ve hash'i değişmez
    ...(typeof record.conditionBefore === 'string'
      ? { conditionBefore: record.conditionBefore }
      : {}),
    ...(typeof record.workHours === 'number' ? { workHours: record.workHours } : {}),
    due: {
      firstOriginalDueOn: input.due.firstOriginalDueOn,
      firstOriginalDueHours: input.due.firstOriginalDueHours,
      dueOn: input.due.dueOn,
      dueHours: input.due.dueHours,
    },
    instruction: { ...input.instruction },
    supersedesRecordId: input.supersedesRecordId,
    ce: { ...input.ce },
    measurements: byId(input.measurements),
    attachments: byId(input.attachments).map((a) => ({ id: a.id, sha256: a.sha256 })),
  };
}
export type WorkRecordContent = ReturnType<typeof workRecordContent>;
